/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegImage } from 'react-icons/fa'
import { FaRegTrashCan } from 'react-icons/fa6'
import { IoIosStarOutline, IoMdCalendar, IoIosArrowBack } from 'react-icons/io'
import { MdLockOutline } from 'react-icons/md'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmDeleteModal } from '../../modals/ConfirmDeleteModal.tsx'
import {
  createLink,
  deleteLink,
  getLinkById,
  updateLink,
} from '../../services/links'
import { getLinks } from '../../store/actions/link.ts'
import { useAppDispatch } from '../../store/index.ts'
import { Button } from '../Button'
import { Input } from '../Input'
import { InputFile } from '../InputFile'
import { Loader } from '../Loader'
import { Toggle } from '../Toggle'
import { LockLink } from './LockLink'
import { Prioritize } from './Prioritize'
import { ScheduleLink } from './ScheduleLink'
import { handleError } from '../../helpers/handleError'
import CropperComponent from '../Cropper/index.tsx'
import { addEditImg, deleteImg } from '../../services/uploadPhoto.ts'
import { set } from 'react-hook-form'

interface FormValues {
  title: string
  link: string
  published: boolean
}

export const LinkForm = () => {
  const [activeIcon, setActiveIcon] = useState<string>('')
  const [id, setId] = useState(0)
  const [enabled, setEnabled] = useState(false)
  const [editData, setEditData] = useState<Links>({
    id: 0,
    title: '',
    link: '',
    timezone: '',
    sensitive: false,
    prioritize: '',
    published: true,
    thumbnail: '',
    addLink: new Date(),
    hideLink: new Date(),
    age: new Date(),
    codeLockDescription: '',
    ageLockExplanation: false,
    animation: '',
    code: 0,
    ageLockDescription: '',
    codeLockExplanation: false,
    order_id: 0,
    createdAt: new Date(),
    updatedAt: new Date(),
  })
  const [errors, setErrors] = useState<FormValues>({
    title: '',
    link: '',
    published: enabled,
  })
  const location = useLocation()
  const [valueInput, setValueInput] = useState({
    title: '',
    link: '',
    published: enabled,
  })
  const [loading, setLoading] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const params = new URLSearchParams(location.search)
  const queryId = params.get('id')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { t } = useTranslation()
  const handleToggle = (enabled: boolean) => {
    if(queryId) {
      saveLink(!enabled)
    }
    setEnabled(!enabled)
  }

  const handleIconClick = (icon: string) => {
    setErrors({ title: '', link: '', published: enabled })

    let hasError = false

    if (!valueInput.title.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        title: 'Title cannot be empty.',
      }))
      hasError = true
    }

    if (!valueInput.link.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        link: 'Link cannot be empty.',
      }))
      hasError = true
    }

    if (hasError) {
      return
    }

    setActiveIcon(icon)
  }

  const handleSendData = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    saveLink()
  }

  const saveLink = async (linkPublished?: boolean) => {
    setErrors({ title: '', link: '', published: linkPublished ||enabled })

    let hasError = false

    if (!valueInput.title.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        title: 'Title cannot be empty.',
      }))
      hasError = true
    }

    if (!valueInput.link.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        link: 'Link cannot be empty.',
      }))
      hasError = true
    }

    if (hasError) {
      return
    }

    const reqData: CreateLinkData = {
      title: valueInput.title,
      link: valueInput.link,
      published: linkPublished || enabled,
    }

    const updateReqData: updateLinkData = {
      title: valueInput.title,
      link: valueInput.link,
      published: linkPublished,
    }

    if (!queryId) {
      const res = await createLink(reqData)

      if (res.status === 201) {
        setId(res.data.id)
        toast.success(t('linkCreateSuccess'))
        navigate('/links')
        return res.data.id
      }
    } else {
      await updateLink(updateReqData, queryId)
        .then(() => {
          toast.success(t('linkUpdateSuccess'))
        })
        .catch((er) => {
          handleError(er)
        })
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setValueInput((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleDeleteLink = () => {
    queryId && deleteLink(queryId)
    navigate('/links')
    toast.success(t('Link was deleted'))

    // refresh links
    setTimeout(() => {
      dispatch(getLinks())
    }, 1000)
  }

  const removeImage = () => {
    deleteImg(queryId || '')
      .then((res) => {
        toast.success(res.message)
        setEditData((prevState) => ({
          ...prevState,
          thumbnail: '',
        }))
      })
      .catch((er) => {
        handleError(er)
      })
  }

  const changeImage = () => {
    setEditData((prevState) => ({
      ...prevState,
      thumbnail: '',
    }))
  }

  useEffect(() => {
    queryId && setLoading(true)
    if (queryId) {
      getLinkById(queryId)
        .then((res) => {
          setEditData(res)
          setValueInput({
            title: res.title || '',
            link: res.link || '',
            published: res.published || false,
          })
          setEnabled(res.published)
          setLoading(false)
        })
        .catch((error) => {
          setLoading(false)
          console.error('Error fetching link by ID:', error)
        })
    }
  }, [queryId])

  if (loading) return <Loader />
  return (
    <>
      <div className="flex items-center justify-between">
        <Link className="text-base text-secondary" to="/links">
          <div className="flex items-center">
            <IoIosArrowBack color="#0000ff" />
            <span className="text-[#0000ff]">{t('links')}</span>
          </div>
        </Link>

        <Toggle onToggle={() => handleToggle(enabled)} enabled={enabled} />
      </div>
      <form
        className="mt-5 w-full sm:w-[200px] lg:w-full"
        onSubmit={(e) => handleSendData(e)}
      >
        <Input
          variant={errors.title ? 'danger' : 'primary'}
          placeholder={t('title')}
          type="text"
          name="title"
          onChange={handleChange}
          helperText={errors.title}
          value={valueInput.title}
        />
        <Input
          variant={errors.link ? 'danger' : 'primary'}
          placeholder={t('link')}
          type="text"
          name="link"
          onChange={handleChange}
          helperText={errors.link}
          value={valueInput.link}
        />

        {/* Link Tabs */}
        {activeIcon === 'image' ? (
          <Button
            text="Save"
            type="submit"
            className="mb-5 w-full text-white"
            disabled={showConfirmDelete}
          />
        ) : null}

        <div className="flex justify-between">
          <div className="flex gap-5">
            <div
              className={`${activeIcon === 'image' || editData.thumbnail ? 'text-[#407BFF]' : 'text-[#676b5f]'} hover:text-[#006eff]`}
            >
              <FaRegImage
                color="inherit"
                className="h-6 w-6 cursor-pointer"
                onClick={() => handleIconClick('image')}
              />
            </div>

            <div
              className={`${activeIcon === 'star' || editData.prioritize ? 'text-[#407BFF]' : 'text-[#676b5f]'} hover:text-[#006eff]`}
            >
              <IoIosStarOutline
                color="inherit"
                className="h-6 w-6 cursor-pointer"
                onClick={() => handleIconClick('star')}
              />
            </div>
            <div
              className={`${activeIcon === 'calendar' || editData.addLink || editData.hideLink ? 'text-[#407BFF]' : 'text-[#676b5f]'} hover:text-[#006eff]`}
            >
              <IoMdCalendar
                color="inherit"
                className="h-6 w-6 cursor-pointer"
                onClick={() => handleIconClick('calendar')}
              />
            </div>
            <div
              className={`${activeIcon === 'lock' || editData.age || editData.code || editData.sensitive ? 'text-[#407BFF]' : 'text-[#676b5f]'} hover:text-[#006eff]`}
            >
              <MdLockOutline
                color="inherit"
                className="h-6 w-6 cursor-pointer"
                onClick={() => handleIconClick('lock')}
              />
            </div>
          </div>
          {queryId ? (
            <div>
              <FaRegTrashCan
                color={activeIcon === 'trash' ? '#407BFF' : '#676b5f'}
                className="h-6 w-6 cursor-pointer"
                onClick={() => handleIconClick('trash')}
              />
            </div>
          ) : null}
        </div>

        {/* Extended form */}
        {activeIcon === 'image' ? (
          <>
            {editData.thumbnail ? (
              <>
                <img
                  src={`${editData.thumbnail}?${new Date().getTime()}`}
                  alt="preview"
                  className="my-5 w-full rounded-md"
                />
                <div className="flex justify-between">
                  <Button
                    type="button"
                    onClick={() => removeImage()}
                    text="Remove"
                    className="mr-2 w-full border-[1px] border-[#676b5f] bg-transparent shadow-none hover:bg-white"
                  />

                  <Button
                    type="button"
                    onClick={() => changeImage()}
                    text="Change"
                    className="ml-2 w-full text-white"
                  />
                </div>
              </>
            ) : (
              <CropperComponent uploadImage={addEditImg} id={queryId} />
            )}
          </>
        ) : // <InputFile
        //   onFileUpload={saveLink}
        //   fallback={
        //     editData.thumbnail
        //       ? `${editData.thumbnail}?${new Date().getTime()}`
        //       : null
        //   }
        // />
        null}
        {activeIcon === 'star' ? (
          <Prioritize
            published={editData.published}
            value={valueInput}
            prioritize={editData.prioritize || ''}
          />
        ) : null}

        {activeIcon === 'calendar' ? (
          <ScheduleLink
            addLink={editData.addLink}
            hideLink={editData.hideLink}
            timezone={editData.timezone}
            value={valueInput}
          />
        ) : null}

        {activeIcon === 'lock' ? (
          <LockLink value={valueInput} editData={editData} />
        ) : null}

        {/* Delete link button */}
        {activeIcon === 'trash' ? (
          <div className="mt-9 flex flex-col">
            <p className="mb-2 text-center text-base">{t('deleteLinkDesc')}</p>
            <Button
              text={t('delete')}
              type="button"
              className="text-white"
              onClick={() => setShowConfirmDelete(true)}
            />

            <ConfirmDeleteModal
              showModal={showConfirmDelete}
              onConfirm={handleDeleteLink}
              onClose={() => setShowConfirmDelete(false)}
            />
          </div>
        ) : null}

        {!activeIcon && (
          <Button
            text={t('save')}
            type="submit"
            className="mt-5 w-full text-white"
            disabled={showConfirmDelete}
          />
        )}
      </form>
    </>
  )
}
